import { createFileRoute } from '@tanstack/react-router';
import { Alert, Avatar, Button, Card, ColorInput, FileInput, Group, Loader, SegmentedControl, Select, Stack, Switch, Text, Title, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconPalette, IconCheck, IconPhoto, IconTrash, IconUpload, IconLayoutSidebar } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import { uploadMediaFile, MEDIA_CAT } from '@/shared/lib/mediaUpload';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import { parseUiTheme, type UiTheme } from '@/shared/lib/uiTheme';
import type { Tenant } from '@/shared/types/entities';

export const Route = createFileRoute('/_app/admin/branding')({ component: BrandingPage });

/** BE v1.8.0: il logo è una relationship JSON:API (tenant.logoMediaFile → media-file). */
function mediaFileBinaryUrl(id: string): string {
  return `/api/media-files/${id}/binary`;
}

function BrandingPage() {
  const qc = useQueryClient();
  const tenantId = useCurrentTenantId();

  const tenantQ = useQuery({
    queryKey: ['tenant-branding', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId, { include: ['logoMediaFile'] }),
  });

  const [plan, setPlan] = useState<string>('free');
  const [primary, setPrimary] = useState<string>('#228BE6');
  const [secondary, setSecondary] = useState<string>('#15AABF');
  const [logoMediaFileId, setLogoMediaFileId] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // --- Tema & layout per-tenant (BE v1.19.0: tenants.uiThemeJson) ---
  const [headerColor, setHeaderColor] = useState<string>('');
  const [sidebarColor, setSidebarColor] = useState<string>('');
  const [headerText, setHeaderText] = useState<'light' | 'dark'>('dark');
  const [sidebarText, setSidebarText] = useState<'light' | 'dark'>('dark');
  const [headerShadow, setHeaderShadow] = useState<boolean>(false);
  const [sidebarShadow, setSidebarShadow] = useState<boolean>(false);
  const [sidebarFixed, setSidebarFixed] = useState<boolean>(true);

  useEffect(() => {
    if (!tenantQ.data) return;
    setPlan(tenantQ.data.plan ?? 'free');
    setPrimary(tenantQ.data.primaryColor ?? '#228BE6');
    setSecondary(tenantQ.data.secondaryColor ?? '#15AABF');
    setLogoMediaFileId(tenantQ.data.logoMediaFile?.id ?? null);
    const t = parseUiTheme(tenantQ.data.uiThemeJson);
    setHeaderColor(t?.headerColor ?? '');
    setSidebarColor(t?.sidebarColor ?? '');
    setHeaderText(t?.headerText === 'light' ? 'light' : 'dark');
    setSidebarText(t?.sidebarText === 'light' ? 'light' : 'dark');
    setHeaderShadow(Boolean(t?.headerShadow));
    setSidebarShadow(Boolean(t?.sidebarShadow));
    setSidebarFixed(t?.sidebarFixed !== false);
  }, [tenantQ.data?.id]);

  const logoUrl = logoMediaFileId ? mediaFileBinaryUrl(logoMediaFileId) : '';

  const save = useMutation({
    mutationFn: () => {
      const uiTheme: UiTheme = {
        headerColor: headerColor || null,
        sidebarColor: sidebarColor || null,
        headerText,
        sidebarText,
        headerShadow,
        sidebarShadow,
        sidebarFixed,
      };
      return updateOne<Tenant>('tenants', {
      id: tenantId,
      attributes: { plan, primaryColor: primary, secondaryColor: secondary, uiThemeJson: JSON.stringify(uiTheme) },
      relationships: {
        logoMediaFile: logoMediaFileId
          ? { data: { type: 'media-files', id: logoMediaFileId } }
          : { data: null },
      },
      });
    },
    onSuccess: () => {
      notifySuccess('Branding salvato. Ricarica la pagina per vedere i nuovi colori applicati.');
      qc.invalidateQueries({ queryKey: ['tenant-branding', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-branding-header', tenantId] });
    },
    onError: (err) => notifyError(err),
  });

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const id = await uploadMediaFile(logoFile, MEDIA_CAT.generic);
      setLogoMediaFileId(id);
      notifySuccess('Logo caricato. Premi "Salva" per collegarlo al tenant.');
    } catch (err) {
      notifyError(err, 'Upload fallito');
    } finally {
      setUploading(false);
    }
  };

  if (tenantQ.isLoading) return <Loader />;

  return (
    <Stack>
      <Group>
        <IconPalette size={28} />
        <Title order={1}>Branding del tenant</Title>
      </Group>
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Personalizza colori e logo del tenant. I colori vengono applicati al tema Mantine al prossimo refresh della pagina.
      </Alert>

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Group justify="space-between">
            <Title order={4}>Logo</Title>
            {logoUrl && <Avatar src={logoUrl} size="lg" radius="sm" />}
          </Group>
          <Group align="flex-end">
            <FileInput
              label="Carica logo (PNG/SVG)"
              placeholder="Seleziona file..."
              accept="image/*"
              value={logoFile}
              onChange={setLogoFile}
              leftSection={<IconPhoto size={16} />}
              style={{ flex: 1 }}
            />
            <Button onClick={() => void uploadLogo()} loading={uploading} disabled={!logoFile} leftSection={<IconUpload size={16} />}>
              Carica
            </Button>
            {logoMediaFileId && (
              <Button variant="light" color="red" onClick={() => setLogoMediaFileId(null)} leftSection={<IconTrash size={16} />}>
                Rimuovi
              </Button>
            )}
          </Group>
          {logoMediaFileId && (
            <Text size="xs" c="dimmed">
              Media file ID: <code>{logoMediaFileId}</code> · servito da <code>/api/media-files/{logoMediaFileId}/binary</code>
            </Text>
          )}
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Title order={4}>Colori tema</Title>
          <Group grow>
            <ColorInput label="Primario" value={primary} onChange={setPrimary} format="hex" />
            <ColorInput label="Secondario" value={secondary} onChange={setSecondary} format="hex" />
          </Group>
          <Group>
            <div style={{ background: primary, width: 80, height: 32, borderRadius: 6 }} />
            <Text size="sm" c="dimmed">Anteprima primario</Text>
            <div style={{ background: secondary, width: 80, height: 32, borderRadius: 6 }} />
            <Text size="sm" c="dimmed">Anteprima secondario</Text>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Group gap="xs">
            <IconLayoutSidebar size={18} />
            <Title order={4}>Tema & layout (header / sidebar)</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Valido per tutti gli utenti del tenant. Si applica al refresh / cambio pagina.
            Lascia i colori vuoti per usare il default del tema.
          </Text>
          <Group grow>
            <ColorInput label="Colore header" value={headerColor} onChange={setHeaderColor} format="hex" placeholder="Default" />
            <ColorInput label="Colore sidebar" value={sidebarColor} onChange={setSidebarColor} format="hex" placeholder="Default" />
          </Group>
          <Group grow align="flex-start">
            <div>
              <Text size="sm" fw={500} mb={4}>Tonalità testo header</Text>
              <SegmentedControl
                fullWidth
                value={headerText}
                onChange={(v) => setHeaderText(v as 'light' | 'dark')}
                data={[{ label: 'Chiara', value: 'light' }, { label: 'Scura', value: 'dark' }]}
              />
            </div>
            <div>
              <Text size="sm" fw={500} mb={4}>Tonalità testo sidebar</Text>
              <SegmentedControl
                fullWidth
                value={sidebarText}
                onChange={(v) => setSidebarText(v as 'light' | 'dark')}
                data={[{ label: 'Chiara', value: 'light' }, { label: 'Scura', value: 'dark' }]}
              />
            </div>
          </Group>
          <Group gap="xl">
            <Switch label="Ombra header" checked={headerShadow} onChange={(e) => setHeaderShadow(e.currentTarget.checked)} />
            <Switch label="Ombra sidebar" checked={sidebarShadow} onChange={(e) => setSidebarShadow(e.currentTarget.checked)} />
            <Tooltip
              label="L'AppShell mantiene la sidebar sempre fissa (vincolo layout Mantine). Campo riservato per evoluzioni future."
              multiline w={260} withArrow
            >
              <Switch label="Sidebar fissa" checked={sidebarFixed} onChange={(e) => setSidebarFixed(e.currentTarget.checked)} disabled />
            </Tooltip>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Title order={4}>Piano corrente</Title>
          <Select
            label="Plan"
            value={plan}
            onChange={(v) => setPlan(v ?? 'free')}
            data={[
              { value: 'free', label: 'Free' },
              { value: 'pro', label: 'Pro' },
              { value: 'enterprise', label: 'Enterprise' },
            ]}
            description="Solo SuperAdmin puo' cambiarlo via questa interfaccia. Per upgrade contatta il commerciale."
          />
        </Stack>
      </Card>

      <Group justify="flex-end">
        <Button size="md" leftSection={<IconCheck size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
          Salva branding
        </Button>
      </Group>
    </Stack>
  );
}
